<template>
  <div class="relative status-dropdown">
    <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Status<span class="text-red-500">*</span></label>
    <button ref="buttonEl" type="button" @click="toggleOpen" @keydown="handleKeydown"
      :class="open ? 'rounded-t-lg border-b-transparent bg-gray-50 dark:bg-white/[0.04]' : 'rounded-lg bg-white dark:bg-[#0d1117]'"
      class="w-full h-[42px] flex items-center justify-between gap-2 px-3.5 text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 transition-shadow cursor-pointer">
      <span class="flex items-center gap-2 min-w-0">
        <span class="w-2 h-2 rounded-full flex-shrink-0" :class="statusDotClass(modelValue)"></span>
        <span class="truncate">{{ selectedOption?.label }}</span>
      </span>
      <svg class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform" :class="open ? 'rotate-180' : ''"
        fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
      </svg>
    </button>

    <!-- Teleported to <body> and positioned with `fixed`, same reasoning as
         CriticalityField: never clipped by an ancestor's overflow:hidden/auto,
         flush against the button's bottom edge for the fused-control look. -->
    <Teleport to="body">
      <div v-if="open" class="fixed inset-0 z-[9998]" @click="open = false"></div>
      <div v-if="open" :style="panelStyle"
        class="fixed z-[9999] bg-white dark:bg-[#1C2128] border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg shadow-lg py-1.5 max-h-72 overflow-y-auto">
        <button v-for="(option, index) in STATUS_OPTIONS" :key="option.value" type="button"
          :ref="(el) => setOptionRef(el, index)"
          @click="select(option.value)" @mouseenter="activeIndex = index"
          :class="index === activeIndex ? 'bg-gray-50 dark:bg-gray-800' : (modelValue === option.value ? 'bg-blue-50 dark:bg-blue-900/20' : '')"
          class="w-full flex items-start gap-2 text-left px-3.5 py-2 transition-colors cursor-pointer">
          <span class="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" :class="statusDotClass(option.value)"></span>
          <span class="min-w-0">
            <p class="text-sm font-medium" :class="modelValue === option.value ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'">
              {{ option.label }}
            </p>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ option.description }}</p>
          </span>
        </button>
      </div>
    </Teleport>
    <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">{{ selectedOption?.description }}</p>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { STATUS_OPTIONS, statusDotClass } from 'controllers/vue_components/organizations/applications/applicationOptions.js'

const props = defineProps({
  modelValue: { type: String, required: true },
})

const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const activeIndex = ref(-1)
const optionRefs = ref([])
const buttonEl = ref(null)
const panelStyle = ref({})

const selectedOption = computed(() => STATUS_OPTIONS.find((option) => option.value === props.modelValue))

function setOptionRef(el, index) {
  if (el) optionRefs.value[index] = el
}

function positionPanel() {
  const rect = buttonEl.value.getBoundingClientRect()
  return {
    top: `${rect.bottom}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  }
}

function toggleOpen() {
  const opening = !open.value
  if (opening) panelStyle.value = positionPanel()
  open.value = opening
  activeIndex.value = opening
    ? STATUS_OPTIONS.findIndex((option) => option.value === props.modelValue)
    : -1
}

function select(value) {
  emit('update:modelValue', value)
  open.value = false
}

watch(activeIndex, (index) => {
  if (index >= 0) optionRefs.value[index]?.scrollIntoView({ block: 'nearest' })
})

function handleKeydown(e) {
  if (!open.value) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleOpen()
    }
    return
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIndex.value = (activeIndex.value + 1) % STATUS_OPTIONS.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = activeIndex.value <= 0 ? STATUS_OPTIONS.length - 1 : activeIndex.value - 1
  } else if (e.key === 'Enter') {
    e.preventDefault()
    if (activeIndex.value >= 0) select(STATUS_OPTIONS[activeIndex.value].value)
  } else if (e.key === 'Escape') {
    open.value = false
  }
}
</script>
